// SPDX-License-Identifier: Apache-2.0
// File: src/io/SysexAndExportPipeline.ts
import type { SynthPreset } from '../presets/FactorySoundLibrary';
import type { SynthParamId } from '../ui/theme/PocketGridRetroTheme';

export interface SavedWorkspaceLayout {
  version: string;
  savedAtUtc: string;
  leftBayShowSliders: boolean;
  centerBayShowSliders: boolean;
  isSplitEnabled: boolean;
  splitMidiNote: number;
  lowerZoneOctave: number;
  upperZoneOctave: number;
  globalOctave: number;
  lowerZonePresetId?: string;
  upperZonePresetId?: string;
  sliderBanks: {
    leftBay: SynthParamId[];
    centerBay: SynthParamId[];
    rightBay: SynthParamId[];
  };
}

const LAYOUT_STORAGE_KEY = 'pocketgrid_saved_workspace_layout_v1';

export function saveWorkspaceLayoutToStorage(layout: SavedWorkspaceLayout): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // Ignore storage quota errors in private browsing
  }
}

export function loadWorkspaceLayoutFromStorage(): SavedWorkspaceLayout | null {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedWorkspaceLayout;
  } catch {
    return null;
  }
}

// Bresenham Integer Accumulator for Euclidean Rhythm Generation E(k, n, r)
export function generateEuclideanPattern(pulsesK: number, stepsN: number, rotationR = 0): boolean[] {
  const n = Math.max(1, Math.min(64, Math.floor(stepsN)));
  const k = Math.max(0, Math.min(n, Math.floor(pulsesK)));
  const result = new Array<boolean>(n).fill(false);
  if (k === 0) return result;
  if (k >= n) return new Array<boolean>(n).fill(true);

  let bucket = n - k;
  for (let i = 0; i < n; i++) {
    bucket += k;
    if (bucket >= n) {
      bucket -= n;
      const idx = ((i + rotationR) % n + n) % n;
      result[idx] = true;
    }
  }
  return result;
}

// Warp Active Step interval calculation: stretches N_active steps evenly across the full bar duration
export function calculateWarpStepDurationMs(
  bpm: number,
  activeStepCount: number,
  totalSteps = 16
): number {
  const safeBpm = Math.max(30, Math.min(300, bpm));
  const sixteenthDurationMs = (60000 / safeBpm) / 4;
  const safeActive = Math.max(1, Math.min(totalSteps, activeStepCount));
  // When Warp Active Step is ON, safeActive steps span the exact time of totalSteps 16th notes
  return (sixteenthDurationMs * totalSteps) / safeActive;
}

// Clean-Room 32-Voice 4104-Byte Yamaha DX7 .syx Bulk Dump Parser
export function parseDx7SysexBank(buffer: Uint8Array): SynthPreset[] {
  if (buffer.length !== 4104) {
    throw new Error(`Expected 4104-byte .syx bank, got ${buffer.length} bytes`);
  }
  if (buffer[0] !== 0xf0 || buffer[1] !== 0x43 || buffer[3] !== 0x09 || buffer[4103] !== 0xf7) {
    throw new Error('Invalid 32-voice bulk SysEx header or footer');
  }

  // Verify 7-bit two's complement checksum over the 4096 payload bytes
  let sum = 0;
  for (let i = 6; i < 6 + 4096; i++) {
    sum += buffer[i] & 0x7f;
  }
  const expectedChecksum = (-sum) & 0x7f;
  const actualChecksum = buffer[4102] & 0x7f;
  if (expectedChecksum !== actualChecksum) {
    throw new Error(`SysEx checksum mismatch: expected ${expectedChecksum}, got ${actualChecksum}`);
  }

  const parsedPresets: SynthPreset[] = [];
  for (let v = 0; v < 32; v++) {
    const vBase = 6 + v * 128;
    const ratios: [number, number, number, number, number, number] = [1, 1, 1, 1, 1, 1];
    const levels: [number, number, number, number, number, number] = [0.8, 0.5, 0.5, 0.5, 0.5, 0.5];

    // Operators are stored Op 6 (0) down to Op 1 (5)
    for (let packedOp = 0; packedOp < 6; packedOp++) {
      const opIdx = 5 - packedOp;
      const opBase = vBase + packedOp * 17;
      const outLevel = (buffer[opBase + 14] & 0x7f) / 99.0;
      const coarse = (buffer[opBase + 15] >> 1) & 0x1f;
      const fine = buffer[opBase + 16] & 0x7f;
      const baseRatio = coarse === 0 ? 0.5 : coarse;
      ratios[opIdx] = Number((baseRatio * (1.0 + fine / 100.0)).toFixed(3));
      levels[opIdx] = Number(Math.max(0.05, Math.min(1.0, outLevel)).toFixed(2));
    }

    const alg = ((buffer[vBase + 110] & 0x1f) + 1);
    const fb = (buffer[vBase + 111] & 0x07) / 7.0;

    let name = '';
    for (let c = 0; c < 10; c++) {
      const code = buffer[vBase + 118 + c] & 0x7f;
      name += code >= 32 && code <= 126 ? String.fromCharCode(code) : ' ';
    }
    const cleanName = name.trim() || `SYX VOICE ${v + 1}`;

    parsedPresets.push({
      id: `syx-imported-${Date.now()}-${v}`,
      name: cleanName,
      category: 'IMPORTED_SYX',
      engineBadge: '6-OP FM',
      featureBadge: `ALG ${alg}`,
      tags: ['SYX', `Alg ${alg}`, 'Imported'],
      favorite: false,
      isSyx: true,
      targetEngineId: 0,
      opRatios: ratios,
      opLevels: levels,
      params: {
        algMorph: Number(((alg - 1) / 31.0).toFixed(2)),
        fmFeedback: Number(fb.toFixed(2)),
        waveFold: 0.05,
      },
    });
  }

  return parsedPresets;
}

// Encode stereo Float32 PCM buffers into a 24-bit Signed PCM WAV file with TPDF Dither
export function encode24BitWavWithTpdfDither(
  leftChannel: Float32Array,
  rightChannel: Float32Array,
  sampleRate = 48000
): Uint8Array {
  const numFrames = Math.min(leftChannel.length, rightChannel.length);
  const numChannels = 2;
  const bytesPerSample = 3; // 24-bit PCM
  const dataBytes = numFrames * numChannels * bytesPerSample;
  const out = new Uint8Array(44 + dataBytes);
  const view = new DataView(out.buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) out[offset + i] = str.charCodeAt(i);
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format = 1
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, 24, true); // 24 bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataBytes, true);

  let prng = 0x13579bdf;
  const nextRand = () => {
    prng ^= prng << 13;
    prng ^= prng >>> 17;
    prng ^= prng << 5;
    return ((prng >>> 0) / 4294967296.0) - 0.5;
  };

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    const samples = [leftChannel[i], rightChannel[i]];
    for (let ch = 0; ch < 2; ch++) {
      const clamped = Math.max(-0.9885, Math.min(0.9885, samples[ch]));
      const tpdfDither = nextRand() - nextRand();
      const s24 = Math.max(
        -8388608,
        Math.min(8388607, Math.floor(clamped * 8388607.0 + tpdfDither + 0.5))
      );
      out[offset] = s24 & 0xff;
      out[offset + 1] = (s24 >> 8) & 0xff;
      out[offset + 2] = (s24 >> 16) & 0xff;
      offset += 3;
    }
  }

  return out;
}
